import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { DotsThreeIcon, HeartIcon } from '@phosphor-icons/react';
import { font } from '../../utils/fontHelper';
import { formatFirebaseDate } from '../../utils/dateUtils';
import { getColors } from '../../tokens/designTokens';
import InitialsAvatar from './InitialsAvatar';
import type { Comment } from '@watchlist/shared';
import { CommentsService } from '@watchlist/shared';
import { glassStyles } from '../../tokens/designTokens';

interface CommentRowProps {
  comment: Comment;
  currentUserId?: string;
  isAdmin?: boolean;
  onDelete: (commentId: string) => void;
  onReport: (commentId: string) => void;
  onUserClick?: (uid: string) => void;
  /** Opens the LikedByBottomSheet with these user ids. Hides the count button if absent. */
  onLikedByClick?: (userIds: string[]) => void;
  /** When true, briefly flashes the row background. Used after a deep-link from a
   *  `comment_liked` push lands on this comment. */
  isHighlighted?: boolean;
  /** Override zIndex for the options Menu and delete Dialog (use when rendered inside a high-zIndex overlay). */
  popoverZIndex?: number;
}

const CommentRow: React.FC<CommentRowProps> = ({
  comment,
  currentUserId,
  isAdmin = false,
  onDelete,
  onReport,
  onUserClick,
  onLikedByClick,
  isHighlighted = false,
  popoverZIndex,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getColors(isDark);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Optimistic like state — applied on top of `comment` until the subscription delivers
  // a doc whose likedBy matches our local view, then cleared. Keeps the UI snappy on
  // web (onSnapshot ~ instant) and tolerable on native (15 s polling).
  const [likeOverride, setLikeOverride] = useState<{ likedBy: string[]; likeCount: number } | null>(null);

  const serverLikedBy: string[] = Array.isArray(comment.likedBy) ? comment.likedBy : [];
  const serverLikeCount: number = typeof comment.likeCount === 'number' ? comment.likeCount : serverLikedBy.length;
  const likedBy = likeOverride ? likeOverride.likedBy : serverLikedBy;
  const likeCount = likeOverride ? likeOverride.likeCount : serverLikeCount;
  const isLiked = Boolean(currentUserId && likedBy.indexOf(currentUserId) !== -1);

  useEffect(() => {
    if (!likeOverride || !currentUserId) return;
    const wantsLiked = likeOverride.likedBy.indexOf(currentUserId) !== -1;
    const serverLiked = serverLikedBy.indexOf(currentUserId) !== -1;
    if (wantsLiked === serverLiked) {
      setLikeOverride(null);
    }
  }, [serverLikedBy, currentUserId, likeOverride]);

  const handleLikeClick = async () => {
    if (!currentUserId) return;
    const nextLikedBy = isLiked
      ? likedBy.filter(u => u !== currentUserId)
      : [...likedBy, currentUserId];
    setLikeOverride({ likedBy: nextLikedBy, likeCount: nextLikedBy.length });
    try {
      await CommentsService.toggleCommentLike(comment.id, currentUserId);
    } catch (err) {
      console.error('CommentRow: toggleCommentLike failed', err);
      setLikeOverride(null);
    }
  };

  const isOwn = Boolean(currentUserId && comment.authorUid === currentUserId);
  const canDelete = isOwn || isAdmin;
  const showMenu = Boolean(currentUserId) && (canDelete || !isOwn);

  const dateLabel = formatFirebaseDate(comment.createdAt, '', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.5,
        px: isHighlighted ? 1 : 0,
        mx: isHighlighted ? -1 : 0,
        alignItems: 'flex-start',
        borderRadius: isHighlighted ? 2 : 0,
        // Brief pink wash on arrival, then fades to transparent over 2.5 s.
        // Mirrors the highlightReview flash used on MovieDetail / review cards.
        animation: isHighlighted ? 'commentHighlight 2.5s ease-out forwards' : 'none',
        '@keyframes commentHighlight': {
          '0%':   { backgroundColor: 'rgba(233,30,99,0.18)' },
          '60%':  { backgroundColor: 'rgba(233,30,99,0.12)' },
          '100%': { backgroundColor: 'rgba(233,30,99,0)' },
        },
      }}
    >
      <ButtonBase
        onClick={() => onUserClick?.(comment.authorUid)}
        disabled={!onUserClick}
        sx={{ borderRadius: '50%', flexShrink: 0, mt: 0.25 }}
      >
        <InitialsAvatar
          src={comment.authorAvatar ?? undefined}
          name={comment.authorName}
          size={32}
        />
      </ButtonBase>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <ButtonBase
            onClick={() => onUserClick?.(comment.authorUid)}
            disabled={!onUserClick}
            sx={{
              borderRadius: 1,
              '&:hover': onUserClick ? { opacity: 0.7 } : undefined,
            }}
          >
            <Typography sx={{
              fontFamily: font(),
              fontWeight: 600,
              fontSize: 13,
              color: colors.text.primary,
            }}>
              {comment.authorName}
            </Typography>
          </ButtonBase>
          {dateLabel && (
            <Typography sx={{
              fontFamily: font(),
              fontSize: 12,
              color: colors.text.secondary,
            }}>
              {dateLabel}
            </Typography>
          )}
          {showMenu && (
            <>
              <IconButton
                onClick={e => setAnchorEl(e.currentTarget)}
                sx={{ color: colors.text.secondary, width: 32, height: 28, flexShrink: 0, ml: -0.5 }}
              >
                <DotsThreeIcon size={18} weight="bold" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                sx={popoverZIndex ? { zIndex: popoverZIndex } : undefined}
                PaperProps={{ sx: { ...glassStyles.menu(isDark) } }}
              >
                {canDelete && (
                  <MenuItem
                    onClick={() => { setAnchorEl(null); setConfirmDeleteOpen(true); }}
                    sx={{ fontFamily: font(), fontSize: 14, color: theme.palette.error.main }}
                  >
                    Delete
                  </MenuItem>
                )}
                {!isOwn && (
                  <MenuItem
                    onClick={() => { setAnchorEl(null); onReport(comment.id); }}
                    sx={{ fontFamily: font(), fontSize: 14 }}
                  >
                    Report
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>

        {(() => {
          const parts = (comment.text || '').trim().split(/\n{2,}/);
          return parts.map((para, i) => (
            <Typography key={i} sx={{
              fontFamily: font(),
              fontSize: 14,
              color: colors.text.primary,
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
              wordBreak: 'break-word',
              mb: i < parts.length - 1 ? 1 : 0,
            }}>
              {para}
            </Typography>
          ));
        })()}
      </Box>

      {/* Like control — count on the left (opens LikedByBottomSheet), heart on the right.
          Sits between the comment body and the 3-dots menu. Both targets are sized
          for comfortable tap (≥32 px). */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {likeCount > 0 && (
          <ButtonBase
            onClick={() => onLikedByClick?.(likedBy)}
            disabled={!onLikedByClick}
            sx={{
              minWidth: 24,
              height: 32,
              px: 0.75,
              borderRadius: 1,
              '&:hover': onLikedByClick
                ? { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' }
                : undefined,
            }}
          >
            <Typography sx={{
              fontFamily: font(),
              fontSize: 12,
              color: colors.text.secondary,
              fontWeight: 500,
            }}>
              {likeCount}
            </Typography>
          </ButtonBase>
        )}
        <IconButton
          disabled={!currentUserId}
          onClick={handleLikeClick}
          sx={{
            width: 36,
            height: 36,
            color: isLiked ? '#e91e63' : colors.text.secondary,
            '&:hover': {
              bgcolor: isLiked
                ? 'rgba(233,30,99,0.1)'
                : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            },
          }}
          aria-label={isLiked ? 'Unlike comment' : 'Like comment'}
        >
          <HeartIcon size={17} weight={isLiked ? 'fill' : 'regular'} />
        </IconButton>
      </Box>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={popoverZIndex ? { zIndex: popoverZIndex } : undefined}
        PaperProps={{
          sx: {
            bgcolor: isDark ? 'rgba(30,30,40,0.98)' : 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: font(), fontWeight: 600, fontSize: 17, color: colors.text.primary }}>
          Delete comment?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: font(), fontSize: 14, color: colors.text.secondary }}>
            This comment will be permanently removed. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            sx={{ fontFamily: font(), fontSize: 14, textTransform: 'none', color: colors.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setConfirmDeleteOpen(false); onDelete(comment.id); }}
            sx={{ fontFamily: font(), fontSize: 14, fontWeight: 600, textTransform: 'none', color: theme.palette.error.main }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommentRow;
